import {Injectable} from '@angular/core';
import {
  ApiCollection,
  ApiResponse,
  Attachment,
  CourseCompletionSummary,
  EmployeeAccessInvitation,
  EmployeeAccessStatus,
  EmployeeCourse,
  EmployeeDetail,
  EmployeePolicy,
  EmployeeRow,
  PolicyAcceptanceSummary
} from '@core/types';
import {CONFIG, ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';
import {HttpHeaders} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async createEmployee(payload: {
    firstName: string;
    lastName: string;
    email: string;
    hiredAt?: string;
    groupId?: string | number | null;
    groupIds?: Array<string | number>;
    isGroupManager?: boolean;
    metadata?: Record<string, any>;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<EmployeeRow>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEES);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(
        url,
        this.mapEmployeePayload(payload),
        headers ? {headers} : undefined
      );
      return {success: true, result: this.mapEmployee(response)};
    } catch (error: any) {
      log.error('Error creating employee:', error);
    }
    return {success: false, message: 'No se pudo crear el empleado.'};
  }

  async getEmployeeDetail(employeeId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<EmployeeDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_DETAIL, {id: employeeId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapEmployeeDetail(response)};
    } catch (error: any) {
      log.error('Error loading employee detail:', error);
    }
    return {success: false, message: 'No se pudo cargar la ficha del empleado.'};
  }

  async updateEmployee(employeeId: string | number, payload: {
    firstName?: string;
    lastName?: string;
    email?: string;
    hiredAt?: string;
    groupId?: string | number | null;
    groupIds?: Array<string | number>;
    isGroupManager?: boolean;
    metadata?: Record<string, any>;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<EmployeeRow>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_DETAIL, {id: employeeId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(
        url,
        this.mapEmployeePayload(payload, {preserveExplicitEmptyGroups: true}),
        {headers, json: false}
      );
      return {success: true, result: this.mapEmployee(response)};
    } catch (error: any) {
      log.error('Error updating employee:', error);
    }
    return {success: false, message: 'No se pudo actualizar el empleado.'};
  }

  async deactivateEmployee(employeeId: string | number): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_DEACTIVATE, {id: employeeId});
      const headers = this.buildHeaders();
      await this.apiService.put(url, {}, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deactivating employee:', error);
    }
    return {success: false, message: 'No se pudo dar de baja al empleado.'};
  }

  async importEmployeesCsv(file: File): Promise<ApiResponse<{created: number; skipped: number; errors: Array<{row?: number; message: string}>}>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEES_IMPORT);
      const formData = new FormData();
      formData.append('file', file, file.name);
      const headers = this.buildHeaders();
      const response: any = await this.apiService.postForm(url, formData, headers ? {headers} : undefined);
      return {
        success: true,
        result: {
          created: Number(response?.created || 0),
          skipped: Number(response?.skipped || 0),
          errors: Array.isArray(response?.errors) ? response.errors : []
        }
      };
    } catch (error: any) {
      log.error('Error importing employees:', error);
    }
    return {success: false, message: 'No se pudo importar el archivo.'};
  }

  async getEmployeeCourses(employeeId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<EmployeeCourse[]>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_COURSES, {id: employeeId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<EmployeeCourse> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<EmployeeCourse>(response).member};
    } catch (error: any) {
      log.error('Error loading employee courses:', error);
    }
    return {success: false, message: 'No se pudieron cargar los cursos del empleado.'};
  }

  async getEmployeePolicies(employeeId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<EmployeePolicy[]>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_POLICIES, {id: employeeId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<EmployeePolicy> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<EmployeePolicy>(response).member};
    } catch (error: any) {
      log.error('Error loading employee policies:', error);
    }
    return {success: false, message: 'No se pudieron cargar las políticas del empleado.'};
  }

  async getEmployeeAccessInvitations(
    filters?: {employeeId?: string | number; status?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<EmployeeAccessInvitation[]>> {
    try {
      const params: Record<string, any> = {
        itemsPerPage: 200
      };

      if (filters?.employeeId !== undefined && filters.employeeId !== null && filters.employeeId !== '') {
        params['employee'] = this.buildIri('employees', filters.employeeId);
      }
      if (filters?.status) {
        params['status'] = filters.status;
      }

      const url = GET_URL(ENDPOINTS.EMPLOYEE_ACCESS_INVITATIONS, params);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {
        success: true,
        result: this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapEmployeeAccessInvitation(item))
      };
    } catch (error: any) {
      log.error('Error loading employee access invitations:', error);
    }
    return {success: false, message: 'No se pudieron cargar las invitaciones de acceso.'};
  }

  async createEmployeeAccessInvitation(
    employeeId: string | number,
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<EmployeeAccessInvitation>> {
    try {
      const url = GET_URL(ENDPOINTS.EMPLOYEE_ACCESS_INVITATIONS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(
        url,
        {employee: this.buildIri('employees', employeeId)},
        headers ? {headers} : undefined
      );
      return {success: true, result: this.mapEmployeeAccessInvitation(response)};
    } catch (error: any) {
      log.error('Error creating employee access invitation:', error);
    }
    return {success: false, message: 'No se pudo generar la invitación de acceso.'};
  }

  async getEmployees(
    page = 1,
    itemsPerPage = 20,
    filters?: {groupId?: string | number; status?: string; search?: string;},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<EmployeeRow[]> & {totalItems?: number}> {
    try {
      const params: Record<string, any> = {
        page,
        itemsPerPage
      };
      if (filters?.groupId) params["groups"] = filters.groupId;
      if (filters?.status) params["status"] = filters.status;
      if (filters?.search) params["search"] = filters.search;

      const url = GET_URL(ENDPOINTS.EMPLOYEES, params);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const normalized = this.apiService.normalizeCollection<any>(response);
      const employees = normalized.member.map((item: any) => this.mapEmployee(item));
      return {success: true, result: employees, totalItems: normalized.totalItems};
    } catch (error: any) {
      log.error('Error loading employees:', error);
    }
    return {success: false, message: 'No se pudieron cargar los empleados.', totalItems: 0};
  }

  async getPolicyAcceptanceSummary(): Promise<ApiResponse<PolicyAcceptanceSummary[]>> {
    try {
      const url = GET_URL(ENDPOINTS.POLICY_ACCEPTANCE_SUMMARY);
      const headers = this.buildHeaders();
      const response: ApiCollection<PolicyAcceptanceSummary> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<PolicyAcceptanceSummary>(response).member};
    } catch (error: any) {
      log.error('Error loading policy acceptance summary:', error);
    }
    return {success: false, message: 'No se pudo cargar el resumen de políticas.'};
  }

  async getCourseCompletionSummary(): Promise<ApiResponse<CourseCompletionSummary[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COURSE_COMPLETION_SUMMARY);
      const headers = this.buildHeaders();
      const response: ApiCollection<CourseCompletionSummary> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<CourseCompletionSummary>(response).member};
    } catch (error: any) {
      log.error('Error loading course completion summary:', error);
    }
    return {success: false, message: 'No se pudo cargar el resumen de cursos.'};
  }

  private mapEmployee(item: any): EmployeeRow {
    const firstName = item.firstName || '';
    const lastName = item.lastName || '';
    const name = (item.name || `${firstName} ${lastName}`.trim()) || 'Sin nombre';
    const groups = Array.isArray(item.groups)
      ? item.groups.map((group: any) => ({
        id: group?.id,
        name: group?.name || 'Grupo'
      })).filter((group: any) => Boolean(group.id))
      : [];
    const group = groups.length
      ? groups.map((entry: any) => entry.name).join(', ')
      : (item.group?.name || item.groupName || item.group || '');
    const hiredAt = item.hiredAt || item.since_at || item.hiredAtDate;
    const since_at = hiredAt ? new Date(hiredAt).toLocaleDateString('es-ES') : '';
    return {
      id: item.id,
      name,
      email: item.email,
      userId: item.userId ?? null,
      userName: item.userName ?? null,
      group,
      groupId: groups[0]?.id || item.group?.id || item.groupId || null,
      groups,
      groupIds: groups.length ? groups.map((groupItem: {id: string | number; name: string}) => groupItem.id) : (item.group?.id || item.groupId ? [item.group?.id || item.groupId] : []),
      userRole: item.userRole || null,
      status: item.status,
      since_at,
      hiredAtRaw: hiredAt || '',
      policies: item.policies ?? item.policyAcceptance ?? 0,
      courses: item.courses ?? item.courseCompletion ?? 0,
      metadata: item.metadata || {},
      avatarUrl: item.avatarUrl || item.photoUrl || item.photo || item.avatar,
      accessStatus: this.resolveEmployeeAccessStatus({
        userId: item.userId ?? null
      }, [])
    };
  }

  private mapEmployeeDetail(item: any): EmployeeDetail {
    return {
      ...this.mapEmployee(item),
      riskLevel: item.riskLevel || item.metadata?.riskLevel,
      attachments: this.mapAttachments(item)
    };
  }

  resolveEmployeeAccessStatus(employee: Partial<EmployeeRow> | null | undefined, invitations: EmployeeAccessInvitation[]): EmployeeAccessStatus {
    const ordered = [...invitations].sort((left, right) => {
      const leftDate = new Date(left.acceptedAt || left.expiresAt || 0).getTime();
      const rightDate = new Date(right.acceptedAt || right.expiresAt || 0).getTime();
      return rightDate - leftDate;
    });

    if (ordered.some(item => item.status === 'active')) return 'active';
    if (ordered.some(item => item.status === 'pending')) return 'pending';
    if (ordered.some(item => item.status === 'rejected')) return 'rejected';
    if (ordered.some(item => item.status === 'expired')) return 'expired';

    return employee?.userRole?.id ? 'active' : 'no_access';
  }

  private mapEmployeeAccessInvitation(item: any): EmployeeAccessInvitation {
    return {
      id: item.id,
      employeeId: item.employeeId ?? item.employee?.id ?? null,
      employeeName: item.employeeName ?? item.employee?.name ?? null,
      userId: item.userId ?? item.user?.id ?? null,
      email: item.email || '',
      status: item.status,
      token: item.token,
      acceptedAt: item.acceptedAt ?? null,
      rejectedAt: item.rejectedAt ?? null,
      expiresAt: item.expiresAt ?? null
    };
  }

  private mapEmployeePayload(payload: {
    firstName?: string;
    lastName?: string;
    email?: string;
    hiredAt?: string;
    groupId?: string | number | null;
    groupIds?: Array<string | number>;
    isGroupManager?: boolean;
    metadata?: Record<string, any>;
  }, options?: {preserveExplicitEmptyGroups?: boolean}) {
    const body: any = {
      ...payload,
      firstName: payload.firstName?.trim(),
      lastName: payload.lastName?.trim(),
      email: payload.email?.trim()
    };

    const groupIds = Array.isArray(payload.groupIds)
      ? payload.groupIds.filter(Boolean)
      : (payload.groupId ? [payload.groupId] : []);

    if (groupIds.length) {
      body.groups = groupIds.map((id: string | number) => this.buildIri('groups', id));
    } else if (options?.preserveExplicitEmptyGroups && ('groupId' in payload || 'groupIds' in payload)) {
      body.groups = [];
    }

    delete body.groupId;
    delete body.groupIds;
    delete body.isGroupManager;

    return body;
  }

  private mapAttachments(item: any): Attachment[] {
    const list = item.attachments || item.attachmentSummaries || item.documents || item.files || [];
    if (!Array.isArray(list)) return [];

    return list.map((entry: any) => ({
      '@context': entry['@context'],
      '@id': entry['@id'],
      '@type': entry['@type'],
      id: entry.id,
      fileName: entry.fileName || entry.name || entry.filename || 'Documento',
      fileSize: entry.fileSize || entry.size || entry.bytes,
      mimeType: entry.mimeType || entry.type,
      filePath: entry.filePath || entry.path,
      createdAt: entry.createdAt || entry.created_at
    }));
  }

  private buildIri(resource: string, id: string | number) {
    const prefix = CONFIG.API_PREFIX.startsWith('/') ? CONFIG.API_PREFIX : `/${CONFIG.API_PREFIX}`;
    const normalized = prefix.endsWith('/') ? prefix : `${prefix}/`;
    return `${normalized}${resource}/${id}`;
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
